import { useRef, useState } from "react";
import { cdssApi, formatApiErrorDetail } from "@/lib/api";
import { computeNews2 } from "@/lib/scores";
import { ADULT_VITALS, vitalAbnormal } from "@/lib/vitals";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2, ArrowRight, ArrowLeft, AlertTriangle, Upload, Gauge,
} from "lucide-react";

export const levelClass = (lvl = "") => {
  const l = lvl.toLowerCase();
  if (l.includes("high")) return "bg-red-50 text-red-700 border-red-200";
  if (l.includes("mod")) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-zinc-100 text-zinc-600 border-zinc-200";
};

export function Badge({ children, className = "" }) {
  return <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold ${className}`}>{children}</span>;
}

export function SectionHeader({ icon: Icon, title, sub }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 text-[#002FA7]">
        <Icon className="h-5 w-5" />
        <span className="text-xs font-semibold uppercase tracking-widest">{sub}</span>
      </div>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl tracking-tight text-zinc-900">{title}</h2>
    </div>
  );
}

export function Loading({ text }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="cdss-loading">
      <Loader2 className="h-8 w-8 animate-spin text-[#002FA7]" />
      <p className="mt-4 text-sm text-zinc-500 max-w-sm">{text}</p>
    </div>
  );
}

export function NavButtons({ onBack, onNext, nextLabel, nextTestId, submit }) {
  return (
    <div className="mt-8 flex items-center justify-between">
      <Button type="button" variant="ghost" onClick={onBack} className="text-zinc-500">
        <ArrowLeft className="mr-2 h-4 w-4" /> Back
      </Button>
      <Button
        type={submit ? "submit" : "button"}
        onClick={onNext}
        data-testid={nextTestId}
        className="bg-[#002FA7] hover:bg-[#002687] text-white h-11 px-7"
      >
        {nextLabel} <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

export function TreatmentList({ title, items, danger }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-5">
      <p className={`text-sm font-semibold mb-2 ${danger ? "text-red-700" : "text-zinc-900"}`}>{title}</p>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={`${it}-${i}`} className={`text-sm flex gap-2 ${danger ? "text-red-700" : "text-zinc-700"}`}>
            <span className={danger ? "text-red-500" : "text-[#002FA7]"}>•</span> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function GlobalRedFlagBanner({ reason }) {
  return (
    <div data-testid="global-red-flag-banner" className="sticky top-0 z-30 mb-6 flex items-start gap-2.5 rounded-md border-2 border-red-500 bg-red-600 p-4 shadow-sm">
      <AlertTriangle className="h-5 w-5 text-white shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-bold text-white uppercase tracking-wide">Red flag — time-critical finding detected</p>
        {reason && <p className="text-sm text-red-50 mt-1">{reason}</p>}
        <p className="text-xs text-red-100 mt-1">Expedite evaluation / referral. This alert stays visible through the rest of the workflow.</p>
      </div>
    </div>
  );
}

export function EmergencyBanner({ reason }) {
  return (
    <div data-testid="emergency-banner" className="mb-6 flex items-start gap-2.5 rounded-md border-2 border-red-400 bg-red-50 p-4">
      <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-red-700">Potential emergency detected — expedite evaluation / referral.</p>
        {reason && <p className="text-sm text-red-600 mt-1">{reason}</p>}
      </div>
    </div>
  );
}

export function VitalsAlert({ vitals, config = ADULT_VITALS }) {
  const abn = config.filter((v) => vitalAbnormal(v, vitals[v.k] || ""));
  if (abn.length === 0) return null;
  return (
    <div data-testid="vitals-emergency-alert" className="mt-4 flex items-start gap-2.5 rounded-md border border-red-300 bg-red-50 p-4">
      <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-red-700">Emergency — abnormal vital sign(s). Consider immediate referral.</p>
        <p className="text-sm text-red-600 mt-1">{abn.map((v) => `${v.label}: ${vitals[v.k]} ${v.unit}`).join("  ·  ")}</p>
      </div>
    </div>
  );
}

export function TriageScore({ vitals, hide }) {
  const r = computeNews2(vitals);
  if (hide || !r) return null;
  return (
    <div data-testid="triage-score" className="mt-4 flex items-center gap-4 rounded-md border border-zinc-200 bg-zinc-50 p-4">
      <div className={`flex h-14 w-14 flex-col items-center justify-center rounded-md text-white ${r.cls}`}>
        <span className="text-lg font-bold leading-none">{r.score}</span>
        <span className="text-[9px] uppercase tracking-wide mt-0.5">NEWS2</span>
      </div>
      <div>
        <p className="text-sm font-semibold text-zinc-900">Early Warning Score: {r.band}</p>
        <p className="text-xs text-zinc-500 mt-0.5">Aggregate of entered vitals (NEWS2-style). A single parameter scoring 3 warrants urgent review.</p>
      </div>
    </div>
  );
}

const SCORE_CHIP_SPECS = [
  { key: "gcs", label: "GCS", val: (s) => `${s.total}/15`, cap: (s) => s.severity },
  { key: "qsofa", label: "qSOFA", val: (s) => s.total, cap: (s) => (s.high_risk ? "high risk" : "low risk") },
  { key: "apache2", label: "APACHE II", val: (s) => s.total, cap: (s) => s.predicted_mortality },
  { key: "sofa", label: "SOFA", val: (s) => s.total, cap: () => "organ dysfunction" },
  { key: "heart", label: "HEART", val: (s) => s.total, cap: (s) => s.risk },
  { key: "timi", label: "TIMI", val: (s) => s.total, cap: () => "cardiac risk" },
  { key: "wells", label: "Wells PE", val: (s) => s.total, cap: (s) => (s.likely ? "PE likely" : "PE unlikely") },
  { key: "curb65", label: "CURB-65", val: (s) => s.total, cap: (s) => s.risk },
];

export function ScoresSummary({ scores }) {
  if (!scores) return null;
  const chips = SCORE_CHIP_SPECS
    .filter((spec) => scores[spec.key])
    .map((spec) => ({ label: spec.label, val: spec.val(scores[spec.key]), cap: spec.cap(scores[spec.key]) }));
  if (chips.length === 0) return null;
  return (
    <div className="mt-8" data-testid="scores-summary">
      <div className="flex items-center gap-2 text-[#002FA7] mb-3"><Gauge className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">Clinical scores</span></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {chips.map(({ label, val, cap }) => (
          <div key={label} className="border border-zinc-200 rounded-md p-3">
            <p className="text-xs text-zinc-400">{label}</p>
            <p className="text-lg font-bold text-zinc-900 leading-tight">{val}</p>
            {cap && <p className="text-xs text-zinc-500 mt-0.5">{cap}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScanButton({ index, onText }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const data = await cdssApi.readReport(file);
      if (data?.text) {
        onText(data.text);
        toast.success("Report scanned & read");
      } else {
        toast.error("No readable results found in the file.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read the report.");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={handle} data-testid={`scan-input-${index}`} />
      <button
        type="button"
        data-testid={`scan-btn-${index}`}
        onClick={() => ref.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#002FA7] hover:underline disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Scan report
      </button>
    </>
  );
}

export function ImagingSection({ imaging, setImaging }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const data = await cdssApi.readImage(file);
      setImaging((arr) => [...arr, { name: file.name, interpretation: data.text || "" }]);
      toast.success("Image interpreted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read the image.");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="mt-6 border border-dashed border-zinc-300 rounded-md p-5" data-testid="imaging-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-zinc-900">Imaging &amp; tracings <span className="font-normal text-zinc-400">(optional)</span></p>
          <p className="text-xs text-zinc-500 mt-0.5">Attach X-ray, ECG or Echo images — interpreted and factored into the final diagnosis. Not required to proceed.</p>
        </div>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={handle} data-testid="imaging-input" />
        <Button type="button" variant="outline" size="sm" className="border-zinc-300 shrink-0" onClick={() => ref.current?.click()} disabled={busy} data-testid="imaging-upload-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Upload image
        </Button>
      </div>
      {imaging.length > 0 && (
        <div className="mt-4 space-y-3">
          {imaging.map((im, i) => (
            <div key={`${im.name}-${i}`} className="bg-white border border-zinc-200 rounded p-3" data-testid={`imaging-item-${i}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-zinc-900">{im.name}</p>
                <button type="button" onClick={() => setImaging((arr) => arr.filter((_, j) => j !== i))} className="text-xs text-red-600 hover:underline" data-testid={`imaging-remove-${i}`}>Remove</button>
              </div>
              <p className="text-xs text-zinc-600 mt-1 whitespace-pre-wrap">{im.interpretation}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
